import { useCallback, useEffect, useState } from 'react';
import { ColumnsWidth, ColumnType, getColumnLabel, VisibleColumns } from '../index';
import { getAverageColumnDataLength, getColumnMultiplier } from '../helper/columnWidth';

interface UseGridColumnsProps {
    columns: ColumnType[];
    gridData: any[];
    gridWidth: number;
    gridParams: any;
    gridParamsApplied: boolean;
    setGridParamsApplied: (value: boolean) => void;
    isDraggable: boolean;
    showCheckboxColumn: boolean;
    fullwidthMode: string;
    gridView: string;
    saveColumns?: (
        columnsWidth: ColumnsWidth,
        visibleColumns: VisibleColumns,
        fullwidthMode: string,
        gridView: string,
        version: number,
    ) => void;
    t: any;
    smartConfigVersion: number;
    readyToDraw: boolean;
    gridWidthApplied: boolean;
    isLoading: boolean;
    setGridView: (value: string) => void;
    widthToggle: boolean;
    setFullwidthMode: (value: string) => void;
    setGridWidthApplied: (value: boolean) => void;
    setReadyToDraw: (value: boolean) => void;
    reloadGridData: () => Promise<void>;
}

interface UseGridColumnsReturn {
    columnsWidth: ColumnsWidth;
    columnsWidthPixels: ColumnsWidth;
    visibleColumns: VisibleColumns;
    visibilityChanged: boolean;
    setVisibilityChanged: (value: boolean) => void;
    setUserInteracted: (value: boolean) => void;
    handleColumnSwitch: (columnName: string, visible: boolean) => void;
    onColumnResize: (columnWidth?: number, dataKey?: string) => void;
    resetGridSettings: () => void;
}

export function useGridColumns({
    isDraggable,
    gridWidth,
    columns,
    showCheckboxColumn,
    gridData,
    saveColumns,
    fullwidthMode,
    t,
    gridView,
    smartConfigVersion,
    gridParams,
    gridParamsApplied,
    setGridParamsApplied,
    readyToDraw,
    gridWidthApplied,
    isLoading,
    setGridView,
    widthToggle,
    setReadyToDraw,
    setGridWidthApplied,
    setFullwidthMode,
    reloadGridData,
}: UseGridColumnsProps): UseGridColumnsReturn {
    const [columnsWidth, setColumnsWidth] = useState<ColumnsWidth>({});
    const [columnsWidthPixels, setColumnsWidthPixels] = useState<ColumnsWidth>({});
    const [visibleColumns, setVisibleColumns] = useState<VisibleColumns>({});
    const [visibilityChanged, setVisibilityChanged] = useState(false);
    const [columnsMultiplier, setColumnsMultiplier] = useState(0);
    const [userInteracted, setUserInteracted] = useState(false);
    const [configLoaded, setConfigLoaded] = useState(false);

    const handleColumnSwitch = (columnName: string, visible: boolean) => {
        setVisibilityChanged(true);
        setUserInteracted(true);
        setVisibleColumns((prevState) => {
            const newState = { ...prevState };
            newState[columnName].visible = visible;

            return newState;
        });
    };

    const reCalcColumnsWidth = (
        newGridWidth: number = gridWidth,
        visColumns: VisibleColumns = visibleColumns,
        forceUserAction: boolean = false,
    ) => {
        if (newGridWidth > 0) {
            const newColumnsWidth: ColumnsWidth = {};

            columns.forEach((column: ColumnType) => {
                if (!column.hidden && visColumns[column.name] && visColumns[column.name].visible) {
                    const title = getColumnLabel(column, t);
                    newColumnsWidth[column.name] = getAverageColumnDataLength(column, gridData, title);
                }
            });
            setColumnsWidth(newColumnsWidth);
            const newMultiplier = getColumnMultiplier(
                newColumnsWidth,
                newGridWidth,
                visColumns,
                isDraggable,
                showCheckboxColumn,
                columns,
            );

            setColumnsMultiplier(newMultiplier);

            if (saveColumns && (forceUserAction || userInteracted || !configLoaded)) {
                saveColumns(newColumnsWidth, visColumns, fullwidthMode, gridView, smartConfigVersion);
            }
        }
    };

    const onColumnResize = useCallback(
        (columnWidth?: number, dataKey?: string) => {
            if (dataKey && columnWidth) {
                const newColumnsWidth = { ...columnsWidth };

                if (columnsMultiplier > 0) {
                    newColumnsWidth[dataKey] = Math.round(columnWidth / columnsMultiplier);
                }

                setColumnsWidth(newColumnsWidth);
                setUserInteracted(true);
                if (saveColumns) {
                    saveColumns(newColumnsWidth, visibleColumns, fullwidthMode, gridView, smartConfigVersion);
                }
            }
        },
        [columnsWidth, columnsWidthPixels, saveColumns, visibleColumns, fullwidthMode, gridView, columnsMultiplier],
    );

    const applyColumnWidth = () => {
        const newColumnsWidthPx: ColumnsWidth = {};

        if (columnsMultiplier > 0) {
            columns.forEach((column: ColumnType) => {
                newColumnsWidthPx[column.name] = columnsMultiplier * columnsWidth[column.name];

                if (column.fixed || column.width) {
                    newColumnsWidthPx[column.name] = column.width ? Number(column.width) : 50;
                }
            });
            setColumnsWidthPixels(newColumnsWidthPx);
        }
    };

    const calculateVisibleColumns = () => {
        const visColumns: VisibleColumns = {};
        columns.forEach((column) => {
            if (column.hidden) {
                return;
            }

            const label = getColumnLabel(column, t);

            const isColumnVisible =
                gridParams && gridParams.columns.hasOwnProperty(column.name)
                    ? !Boolean(gridParams.columns[column.name].hidden)
                    : (column.visible ?? true);

            visColumns[column.name] = {
                label: label > '' ? label : column.name,
                hidable: column.hidable ?? true,
                visible: isColumnVisible,
            };
        });

        setVisibleColumns(visColumns);
    };

    const resetGridSettings = useCallback(() => {
        setUserInteracted(true);
        reCalcColumnsWidth(gridWidth, visibleColumns, true);
    }, [saveColumns, visibleColumns, fullwidthMode, reloadGridData, gridView, gridWidth]);

    useEffect(() => {
        if (columnsMultiplier > 0) {
            applyColumnWidth();
        }
    }, [columnsWidth, columnsMultiplier]);

    useEffect(() => {
        if (gridData.length > 0 && Object.keys(columnsWidthPixels).length === 0 && !gridParamsApplied) {
            reCalcColumnsWidth();
        }
    }, [gridData, columnsWidthPixels, gridParamsApplied]);

    useEffect(() => {
        if (visibilityChanged) {
            if (gridParamsApplied) {
                reCalcColumnsWidth();
            }
            setVisibilityChanged(false);
        }
    }, [visibilityChanged]);

    useEffect(() => {
        if (
            gridWidth > 0 &&
            gridData.length > 0 &&
            (!gridParams || !gridParams.version || gridParams.version != smartConfigVersion)
        ) {
            reCalcColumnsWidth(gridWidth, visibleColumns, true);
        }
    }, [gridData, gridParams, gridWidth, visibleColumns]);

    useEffect(() => {
        if (
            readyToDraw &&
            gridWidth > 0 &&
            columns.length > 0 &&
            Object.keys(columnsWidth).length === 0 &&
            Object.keys(visibleColumns).length > 0 &&
            !gridParamsApplied
        ) {
            reCalcColumnsWidth(gridWidth, visibleColumns, false);
        }
    }, [readyToDraw, gridWidth, columnsWidth, visibleColumns, columns, gridParamsApplied]);

    useEffect(() => {
        if (gridParamsApplied && gridWidthApplied) {
            const newMultiplier = getColumnMultiplier(
                columnsWidth,
                gridWidth,
                visibleColumns,
                isDraggable,
                showCheckboxColumn,
                columns,
            );
            setColumnsMultiplier(newMultiplier);
        }
    }, [
        gridParamsApplied,
        gridWidth,
        gridWidthApplied,
        columnsWidth,
        visibleColumns,
        isDraggable,
        showCheckboxColumn,
        columns,
    ]);

    useEffect(() => {
        if (columns.length > 0) {
            calculateVisibleColumns();
            setVisibilityChanged(true);
        }
    }, [JSON.stringify(columns)]);

    useEffect(() => {
        if (!isLoading && columns.length > 0 && !gridParamsApplied) {
            if (gridParams && gridParams.version && gridParams.version == smartConfigVersion) {
                setConfigLoaded(true);
            }

            calculateVisibleColumns();

            if (gridWidth > 0) {
                if (
                    gridParams &&
                    !gridParamsApplied &&
                    gridParams.version &&
                    gridParams.version == smartConfigVersion
                ) {
                    const gridParamsWidth: ColumnsWidth = {};
                    columns.forEach((column) => {
                        if (!column.hidden) {
                            if (gridParams.columns.hasOwnProperty(column.name)) {
                                const width = Number(gridParams.columns[column.name].width);
                                gridParamsWidth[column.name] = width > 0 ? width : 5;
                            } else {
                                gridParamsWidth[column.name] = 5;
                            }
                        }
                    });
                    setColumnsWidth(gridParamsWidth);

                    setGridView(gridParams.gridView ?? 'default');

                    if (widthToggle && gridParams.fullwidthMode > '' && gridParams.fullwidthMode != fullwidthMode) {
                        setFullwidthMode(gridParams.fullwidthMode);
                        setGridWidthApplied(false);
                    }
                }

                setGridParamsApplied(true);
            }
            setReadyToDraw(true);
        }
    }, [gridWidth, isLoading, gridParams, columns]);

    return {
        columnsWidth,
        columnsWidthPixels,
        visibleColumns,
        visibilityChanged,
        setVisibilityChanged,
        setUserInteracted,
        handleColumnSwitch,
        onColumnResize,
        resetGridSettings,
    };
}
